import React, { useEffect, useState } from 'react';
import { DefaultButton, Label, PrimaryButton, Stack, Text, TextField } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import type { IZammadConfiguration } from './zammad.types';

interface IZammadSettingsFormProps {
    zammadBaseUrl: string;
    isOpen: boolean;
    updateZammadConfig: (config: IZammadConfiguration) => Promise<OrganizationDto>;
    onSaveSuccess: () => void;
    onCancel: () => void;
}

function isVerificationFailure(error: unknown): boolean {
    if (typeof error !== 'object' || error === null || !('response' in error)) return false;
    return (error as { response?: { status?: number } }).response?.status === 422;
}

function httpStatusOf(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
    return (error as { response?: { status?: number } }).response?.status;
}

// Shared by the save path (here), ZammadSettings.tsx's deactivation path and the widget's connect
// button, so any failed Zammad write - whichever component triggered it - resolves to the same
// user-visible message instead of one path surfacing errors and the other swallowing them.
//
// `isDirectBrowserMode` narrows the two extra arms to the browser-direct connect flow, where the
// error comes from the customer's Zammad rather than from our own backend. The admin-facing,
// proxied Campus Settings paths never pass it, so what they show is unchanged: 422 stays the
// verification message and everything else stays the generic one. Reading a statusless failure as
// "we can't reach your Zammad" would be a lie there — the request that failed was to us.
export function resolveZammadErrorMessage(error: unknown, isDirectBrowserMode = false): string {
    // 422 = credential verification failed. One localized message only — do not append the
    // backend body (it repeats the same idea, e.g. "Could not verify Zammad base URL and token").
    if (isVerificationFailure(error)) {
        return IvicosStrings.zammadSettingsVerifyFailedError;
    }

    if (isDirectBrowserMode) {
        const status = httpStatusOf(error);

        // A mistyped or revoked personal token: Zammad itself answers 401, where our proxy would
        // have answered 422. Same meaning, so the same string — it already says the credentials
        // could not be verified.
        if (status === 401) {
            return IvicosStrings.zammadSettingsVerifyFailedError;
        }

        // No status at all: off the customer's network, VPN down, blocked CORS preflight, dead
        // server — indistinguishable from the browser, and the commonest direct-mode failure of
        // the two. Name the reachability problem instead of "Something went wrong."
        if (status === undefined) {
            return IvicosStrings.zammadWidgetUnreachableTitle;
        }
    }

    return IvicosStrings.somethingWentWrong;
}

export const ZammadSettingsForm: React.FC<IZammadSettingsFormProps> = ({ zammadBaseUrl, isOpen, updateZammadConfig, onSaveSuccess, onCancel }) => {
    const [baseUrl, setBaseUrl] = useState(zammadBaseUrl);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // The Panel (and this form inside it) is mounted unconditionally by ZammadSettings.tsx even
    // while closed, so if `zammadBaseUrl` resolves after this component already mounted (org data
    // still loading on first render), re-sync the field instead of freezing it at the initial
    // (possibly empty) value.
    useEffect(() => {
        setBaseUrl(zammadBaseUrl);
    }, [zammadBaseUrl]);

    // Discard unsaved drafts when the panel is closed via Cancel or the X — never persist
    // credentials unless Save succeeded.
    useEffect(() => {
        if (!isOpen) {
            setErrorMessage(null);
            setBaseUrl(zammadBaseUrl);
            setIsSaving(false);
        }
    }, [isOpen, zammadBaseUrl]);

    const handleSave = async (): Promise<void> => {
        setIsSaving(true);
        setErrorMessage(null);
        try {
            // updateZammadConfig already updates the shared org state (setOrg) on success, so no
            // follow-up fetchCampus() is needed here — an extra call would only add a redundant
            // request that, if it happened to fail on its own, would surface a false error after
            // a save that actually succeeded.
            await updateZammadConfig({ is_zammad_enable: true, zammad_base_url: baseUrl });
            onSaveSuccess();
        } catch (error) {
            console.error('Failed to save Zammad settings:', resolveZammadErrorMessage(error));
            setErrorMessage(resolveZammadErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Stack verticalAlign="space-between" styles={{ root: { height: '100%', minHeight: 'calc(100vh - 110px)' } }}>
            <Stack tokens={{ childrenGap: 16 }} style={{ marginTop: 16 }}>
                <Stack tokens={{ childrenGap: 4 }}>
                    <Label>{IvicosStrings.zammadSettingsBaseUrlLabel}</Label>
                    <TextField type="url" value={baseUrl} onChange={(_ev, newValue) => setBaseUrl(newValue ?? '')} data-test-id="zammad-base-url" />
                </Stack>
                {errorMessage && <Text style={{ color: '#A80000' }}>{errorMessage}</Text>}
            </Stack>
            <Stack horizontal tokens={{ childrenGap: 16 }} styles={{ root: { width: '100%', paddingBottom: 8 } }}>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <DefaultButton
                        text={IvicosStrings.zammadSettingsCancelButton}
                        onClick={onCancel}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="zammad-cancel-button"
                    />
                </Stack.Item>
                <Stack.Item grow styles={{ root: { flexBasis: 0 } }}>
                    <PrimaryButton
                        text={IvicosStrings.zammadSettingsSaveButton}
                        onClick={handleSave}
                        disabled={isSaving || !baseUrl}
                        styles={{ root: { width: '100%' } }}
                        data-test-id="zammad-save-button"
                    />
                </Stack.Item>
            </Stack>
        </Stack>
    );
};
